import { FC, useEffect, useState } from 'react';
import axios from 'axios';
import { Info, ShieldCheck, Download, Trash, ClockCounterClockwise, Heart, HeartBreak } from '@phosphor-icons/react';
import { useAppSelector } from '../../../store';
import CustomButton from '../../../shared/CustomButton/CustomButton';
import Typhography from '../../../shared/Typhography/Typhography';
import { baseUrl } from '../../../hooks/useEndpoints';
import styles from './GdprSection.module.scss';

/**
 * Sezione GDPR nel ProfilePage atleta.
 * Spiegazione semplice + 2 azioni: Scarica dati / Cancella account.
 */
export const GdprSection: FC = () => {
  const selectedEntity = useAppSelector((st) => st.me.selectedEntity);
  const userId = (selectedEntity as any)?._id as string | undefined;

  const [explainOpen, setExplainOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [deleteModal, setDeleteModal] = useState<
    null | 'confirm1' | 'confirm2' | 'done'
  >(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [auditTrail, setAuditTrail] = useState<null | {
    count: number;
    events: Array<{
      timestamp: string;
      type: string;
      endpoint?: string;
      collection?: string;
      actorEmail?: string;
      actorType?: string;
    }>;
  }>(null);
  const [auditLoading, setAuditLoading] = useState(false);
  const [consentStatus, setConsentStatus] = useState<null | {
    healthData: { accepted: boolean; acceptedAt: string | null };
  }>(null);
  const [revokeLoading, setRevokeLoading] = useState(false);

  const fetchConsentStatus = async () => {
    if (!userId) return;
    try {
      const r = await axios.get(
        `${baseUrl}/users/${userId}/gdpr/consent-status`,
      );
      setConsentStatus(r.data);
    } catch {
      setConsentStatus(null);
    }
  };

  useEffect(() => {
    fetchConsentStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const handleRevokeHealth = async () => {
    if (!userId) return;
    if (
      !window.confirm(
        'Vuoi revocare il consenso al trattamento dei dati salute? Da questo momento non potrai più compilare Hooper, FCR/HRV, BodyMap. I dati raccolti finora restano in archivio finché non chiedi la cancellazione account.',
      )
    )
      return;
    setRevokeLoading(true);
    try {
      await axios.delete(
        `${baseUrl}/users/${userId}/gdpr/consent/health-data`,
      );
      await fetchConsentStatus();
    } catch {
      alert('Errore. Riprova fra qualche minuto.');
    } finally {
      setRevokeLoading(false);
    }
  };

  const loadAuditTrail = async () => {
    if (!userId) return;
    setAuditLoading(true);
    try {
      const r = await axios.get(
        `${baseUrl}/users/${userId}/gdpr/audit-trail?days=90`,
      );
      setAuditTrail(r.data);
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 404) {
        alert(
          'Cronologia disponibile solo per profili Atleta. Cambia profilo dalla barra in alto.',
        );
      } else {
        alert('Errore caricamento cronologia. Riprova fra qualche minuto.');
      }
    } finally {
      setAuditLoading(false);
    }
  };

  const handleDownload = async () => {
    if (!userId) return;
    setDownloading(true);
    try {
      const res = await axios.get(`${baseUrl}/users/${userId}/gdpr/export`, {
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `i-miei-dati-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 429) {
        alert(
          'Hai già richiesto i tuoi dati troppe volte oggi. Riprova fra qualche ora.',
        );
      } else if (status === 404) {
        alert(
          'Per scaricare i tuoi dati personali devi essere loggato con il profilo Atleta. Cambia profilo dalla barra in alto e riprova.',
        );
      } else {
        alert(
          'Errore durante il download. Contatta privacy@leopartmove.com',
        );
      }
    } finally {
      setDownloading(false);
    }
  };

  const handleDelete = async () => {
    if (!userId) return;
    setDeleteError(null);
    try {
      await axios.delete(
        `${baseUrl}/users/${userId}/gdpr/erasure?confirm=true`,
      );
      setDeleteModal('done');
    } catch (err: any) {
      const status = err?.response?.status;
      const msg = err?.response?.data?.message;
      if (status === 429) {
        setDeleteError(
          'Richiesta di cancellazione già inviata. Verrà processata.',
        );
      } else {
        setDeleteError(
          msg ?? 'Errore. Contatta privacy@leopartmove.com',
        );
      }
    }
  };

  return (
    <div className={styles.section}>
      <div className={styles.header}>
        <ShieldCheck size={20} weight="duotone" className={styles.icon} />
        <Typhography size="bold-16">I tuoi dati e privacy</Typhography>
      </div>

      <Typhography size="regular-14" className={styles.intro}>
        LeoPart raccoglie dati su di te per offrirti il servizio. Il GDPR
        è la legge europea che ti dà il controllo sui tuoi dati: puoi
        scaricarli, modificarli o cancellarli quando vuoi.
      </Typhography>

      <button
        type="button"
        className={styles.explainBtn}
        onClick={() => setExplainOpen((v) => !v)}
      >
        <Info size={14} /> {explainOpen ? 'Nascondi' : 'Cosa è il GDPR?'}
      </button>

      {explainOpen && (
        <div className={styles.explainBox}>
          <p>
            <strong>GDPR</strong> (Regolamento Generale sulla Protezione
            dei Dati) è la legge europea — in vigore dal 2018 — che
            regola come aziende e app possono usare i tuoi dati personali.
          </p>
          <p>
            <strong>Cosa significa per te in LeoPart?</strong>
          </p>
          <ul>
            <li>
              <strong>Sai cosa raccogliamo</strong>: nome, email, foto,
              dati salute (sonno, fatica, recovery), allenamenti,
              sessioni GPS, test fisici.
            </li>
            <li>
              <strong>Hai il controllo</strong>: puoi vedere tutti i tuoi
              dati, correggere ciò che è sbagliato, scaricarli in formato
              leggibile o chiedere di cancellarli definitivamente.
            </li>
            <li>
              <strong>Trasparenza</strong>: i dati sono ospitati in
              Europa (Frankfurt). Sub-fornitori (Google, AWS, Anthropic
              per AI) elencati nella{' '}
              <a href="/privacy" target="_blank" rel="noreferrer">
                Privacy Policy
              </a>
              .
            </li>
            <li>
              <strong>Reclamo</strong>: se pensi che stiamo facendo
              qualcosa di sbagliato puoi scrivere a{' '}
              <a href="mailto:privacy@leopartmove.com">
                privacy@leopartmove.com
              </a>{' '}
              o segnalarlo al{' '}
              <a
                href="https://www.garanteprivacy.it"
                target="_blank"
                rel="noreferrer"
              >
                Garante Privacy
              </a>
              .
            </li>
          </ul>
        </div>
      )}

      <div className={styles.actionRow}>
        <div>
          <Typhography size="bold-14">Scarica i tuoi dati</Typhography>
          <Typhography size="regular-12" className={styles.actionDesc}>
            Ricevi un file JSON con tutti i dati che abbiamo su di te.
            Massimo 3 download al giorno.
          </Typhography>
        </div>
        <CustomButton
          color="outline"
          onClick={handleDownload}
          isLoading={downloading}
          disabled={downloading || !userId}
        >
          <Download size={14} /> Scarica
        </CustomButton>
      </div>

      {consentStatus && (
        <div className={styles.actionRow}>
          <div>
            <Typhography size="bold-14">
              Consenso dati salute (art. 9 GDPR)
            </Typhography>
            <Typhography size="regular-12" className={styles.actionDesc}>
              {consentStatus.healthData.accepted
                ? `Hai dato il consenso il ${new Date(consentStatus.healthData.acceptedAt!).toLocaleDateString('it-IT')}. Puoi revocarlo in qualsiasi momento (art. 7(3) GDPR). I dati raccolti finora restano nel nostro archivio finché non chiedi la cancellazione dell'account.`
                : 'Consenso al trattamento dati salute non attivo. Non puoi compilare Hooper/FCR/HRV/BodyMap finché non lo riattivi dal flusso di onboarding.'}
            </Typhography>
          </div>
          {consentStatus.healthData.accepted && (
            <CustomButton
              color="outline"
              typeText="danger"
              onClick={handleRevokeHealth}
              isLoading={revokeLoading}
              disabled={revokeLoading}
            >
              <HeartBreak size={14} /> Revoca
            </CustomButton>
          )}
        </div>
      )}

      <div className={styles.actionRow}>
        <div>
          <Typhography size="bold-14">Cronologia accessi (ultimi 90gg)</Typhography>
          <Typhography size="regular-12" className={styles.actionDesc}>
            Vedi chi ha visualizzato o modificato i tuoi dati. Trasparenza
            ai sensi dell&apos;art. 15 GDPR.
          </Typhography>
        </div>
        <CustomButton
          color="outline"
          onClick={loadAuditTrail}
          isLoading={auditLoading}
          disabled={auditLoading || !userId}
        >
          <ClockCounterClockwise size={14} /> Mostra
        </CustomButton>
      </div>

      {auditTrail && (
        <div className={styles.auditList}>
          <strong>{auditTrail.count} eventi</strong>
          {auditTrail.events.length === 0 && (
            <p className={styles.muted}>Nessun evento nei 90 giorni.</p>
          )}
          {auditTrail.events.slice(0, 50).map((e, i) => (
            <div key={i} className={styles.auditRow}>
              <span className={styles.auditTime}>
                {new Date(e.timestamp).toLocaleString('it-IT')}
              </span>
              <span className={styles.auditType}>{e.type}</span>
              <span className={styles.auditEndpoint}>
                {e.endpoint ?? e.collection ?? '—'}
              </span>
              {e.actorEmail && (
                <span className={styles.auditActor}>
                  da: {e.actorEmail}
                </span>
              )}
            </div>
          ))}
          {auditTrail.events.length > 50 && (
            <p className={styles.muted}>
              Mostrati primi 50 su {auditTrail.events.length}.
            </p>
          )}
        </div>
      )}

      <div className={styles.actionRow}>
        <div>
          <Typhography size="bold-14">Cancella account</Typhography>
          <Typhography size="regular-12" className={styles.actionDesc}>
            Cancella permanentemente i tuoi dati. Operazione
            irreversibile. I dati anonimi aggregati per le statistiche
            del team vengono conservati senza identificarti.
          </Typhography>
        </div>
        <CustomButton
          color="outline"
          typeText="danger"
          onClick={() => setDeleteModal('confirm1')}
          disabled={!userId}
        >
          <Trash size={14} /> Cancella
        </CustomButton>
      </div>

      {deleteModal === 'confirm1' && (
        <div className={styles.modalBackdrop}>
          <div className={styles.modal}>
            <Typhography size="bold-20">
              Vuoi davvero cancellare il tuo account?
            </Typhography>
            <p>
              Questa azione cancellerà <strong>permanentemente</strong>:
            </p>
            <ul>
              <li>Il tuo profilo (nome, email, foto, telefono)</li>
              <li>
                Tutti i tuoi dati salute (Hooper, recovery, FCR/HRV,
                BodyMap, fastidi)
              </li>
              <li>Le tue notifiche, dispositivi push, preferenze</li>
            </ul>
            <p>
              I tuoi dati di allenamento storici (workout, sessioni GPS,
              test) vengono <strong>anonimizzati</strong>: rimangono
              nelle statistiche del team ma senza più collegamento al
              tuo nome.
            </p>
            <p>
              <em>Questa azione è irreversibile.</em>
            </p>
            <div className={styles.modalActions}>
              <CustomButton
                color="outline"
                onClick={() => setDeleteModal(null)}
              >
                Annulla
              </CustomButton>
              <CustomButton
                color="primary"
                typeText="danger"
                onClick={() => setDeleteModal('confirm2')}
              >
                Continua
              </CustomButton>
            </div>
          </div>
        </div>
      )}

      {deleteModal === 'confirm2' && (
        <div className={styles.modalBackdrop}>
          <div className={styles.modal}>
            <Typhography size="bold-20">Ultima conferma</Typhography>
            <p>
              Sei sicuro al 100%? Una volta confermato, non puoi tornare
              indietro.
            </p>
            {deleteError && (
              <p className={styles.error}>{deleteError}</p>
            )}
            <div className={styles.modalActions}>
              <CustomButton
                color="outline"
                onClick={() => setDeleteModal(null)}
              >
                No, annulla
              </CustomButton>
              <CustomButton
                color="primary"
                typeText="danger"
                onClick={handleDelete}
              >
                Sì, cancella tutto
              </CustomButton>
            </div>
          </div>
        </div>
      )}

      {deleteModal === 'done' && (
        <div className={styles.modalBackdrop}>
          <div className={styles.modal}>
            <Typhography size="bold-20">Account cancellato</Typhography>
            <p>
              I tuoi dati personali sono stati rimossi. A breve verrai
              disconnesso. Grazie per aver usato LeoPart.
            </p>
            <div className={styles.modalActions}>
              <CustomButton
                color="primary"
                onClick={() => {
                  window.location.href = '/login';
                }}
              >
                Esci
              </CustomButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
